<template>
  <div class="">
    <div
      v-for="(aufgabe, id) in aufgabenData"
      :key="'aufgabe' + aufgabe.id"
      style="display: flex; gap: 8px; margin: 8px 0px"
    >
      <span
        :id="`aufgabe-widget-${aufgabe.id}`"
        @click="aufgabe.id != openAufgabenId && onClickBadge(aufgabe)"
        :class="['aufgabe-pill', 'hoverAufgabenWidget', { 'current-aufgabe': aufgabe.id == openAufgabenId }]"
        v-b-tooltip.hover.left="{
          title: ansicht === 'vorgang-overview' ? aufgabe.beschreibung || '' : '',
          html: true,
          customClass: 'aufgaben-widget-tooltip',
        }"
      >
        <span class="aufgabe-pill__nummer">{{ id + 1 }}</span>
        <span>
          {{ aufgabe.kategorieLabel }}
        </span>
        <component
          v-if="statusIcons[aufgabe.status]"
          :is="statusIcons[aufgabe.status].icon"
          :size="16"
          :style="{ color: statusIcons[aufgabe.status].color }"
          class="flex-shrink-0"
        />

        <span>{{ aufgabe.bearbeiter ? username(aufgabe.bearbeiter) : getTeamLabel(aufgabe.team) }}</span>
        <span
          v-if="aufgabe.status !== 'Erledigt'"
          class="pill-badge pill-badge--small"
          :style="faelligkeitStyle(aufgabe.faelligkeitsdatum)"
        >
          {{ aufgabe.faelligkeitFormatted }} ({{ aufgabe.werktageBisFaelligkeit }})
        </span>
      </span>
      <b-tooltip
        v-if="ansicht === 'reisetermine-view' && aufgabe.vorgaenge && aufgabe.vorgaenge.length > 0"
        :target="`aufgabe-widget-${aufgabe.id}`"
        triggers="hover"
        placement="right"
        custom-class="aufgaben-widget-tooltip"
      >
        <table>
          <thead>
            <tr>
              <td><b>BNR</b></td>
              <td><b>Teilnehmende</b></td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="vorgang in aufgabe.vorgaenge" :key="vorgang.id">
              <td>{{ vorgang.id }}</td>
              <td>
                <div
                  v-for="(teilnehmer, index) in vorgang.teilnehmers"
                  :key="`${vorgang.id}-${index}-${teilnehmer.name}`"
                >
                  {{ teilnehmer.name }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </b-tooltip>
      <span
        v-if="
          (ansicht === 'zvc-overview' ||
            ansicht === 'reisetermine-view' ||
            ansicht === 'reisetermin-view-releasepruefung') &&
          id === aufgabenData.length - 1
        "
        class="aufgabe-pill"
        v-b-tooltip.hover
        title="Aufgabe erstellen"
        @click="plusSignClicked(aufgabe)"
      >
        <Plus :size="16" />
      </span>
    </div>
  </div>
</template>
<script>
import { parseISO, format, isBefore } from 'date-fns';
import { SET_AUFGABEN_VERLINKUNG } from '@/core/produkte/reisetermine/stores/zvc.module';
import {
  flugstammdatenAufgabenKategorien,
  flugplanungAufgabenKategorien,
  aufgabenStatusColors,
  grauPillColors,
} from '@/core/common/helpers/utils.js';
import { mapGetters } from 'vuex';
import { CircleAlert, CircleCheck, CircleDotDashed, Clock, Plus } from 'lucide-vue';

// Status-Icon und -Farbe je Aufgabenstatus
const statusIcons = {
  Offen: { icon: CircleAlert, color: aufgabenStatusColors.Offen.borderColor },
  'In Arbeit': { icon: CircleDotDashed, color: aufgabenStatusColors.InArbeit.borderColor },
  Pending: { icon: Clock, color: aufgabenStatusColors.Pending.borderColor },
  Erledigt: { icon: CircleCheck, color: aufgabenStatusColors.Erledigt.borderColor },
};

export default {
  components: { Plus },
  props: {
    aufgaben: { type: Array, required: true },
    filter: { type: String },
    ansicht: { type: String },
    onPlusSignClicked: { type: Function, default: () => {} },
    data: { type: Object, default: null },
    openAufgabenId: { type: Number, default: null },
    reiseterminObj: { type: Object, default: null },
  },
  data() {
    return { statusIcons };
  },
  computed: {
    ...mapGetters([
      'getKategorienDestinationYield',
      'getKategorienLabel',
      'getKategorienReleaseprozess',
      'getFlugTeamUsersIds',
    ]),
    aufgabenData() {
      const formattedAufgaben = this.aufgaben.map(aufgabe => ({
        ...aufgabe,
        kategorieLabel: this.getKategorienLabel(aufgabe.kategorie),
        status: aufgabe.status === 'InArbeit' ? 'In Arbeit' : aufgabe.status,
        faelligkeitFormatted: format(parseISO(aufgabe.faelligkeitsdatum), 'dd.MM.'),
      }));
      if (this.filter === 'zvc-overview') {
        return formattedAufgaben.filter(aufgabe =>
          this.getKategorienDestinationYield.includes(aufgabe.kategorie)
        );
      } else if (this.filter === 'flugstammdaten') {
        return formattedAufgaben.filter(
          aufgabe =>
            flugstammdatenAufgabenKategorien
              .filter(kategorie => kategorie !== 'NPruefung')
              .includes(aufgabe.kategorie) ||
            (aufgabe.kategorie === 'NPruefung' && !aufgabe.beschreibung.includes('+'))
        );
      } else if (this.filter === 'flugplanung') {
        return formattedAufgaben.filter(aufgabe => flugplanungAufgabenKategorien.includes(aufgabe.kategorie));
      } else if (this.filter === 'only-flug-team') {
        return formattedAufgaben.filter(
          aufgabe =>
            this.getFlugTeamUsersIds.includes(aufgabe.bearbeiter) ||
            aufgabe.team == 'Flight Operations Management'
        );
      } else {
        return formattedAufgaben;
      }
    },
  },
  methods: {
    faelligkeitStyle(faelligkeitsdatum) {
      const { background, color } = this.isFaelligkeitInPast(faelligkeitsdatum)
        ? aufgabenStatusColors.Offen
        : grauPillColors;
      return { background, color };
    },
    onClickBadge(aufgabe) {
      if (
        this.ansicht === 'fvc-overview' ||
        this.ansicht === 'zvc-overview' ||
        this.ansicht === 'sidebar-flugplanung' ||
        this.ansicht === 'sidebar-terminplanung' ||
        this.ansicht === 'flugstammdaten-overview' ||
        this.ansicht === 'aufgabe-details' ||
        this.ansicht === 'vorgang-overview' ||
        this.ansicht === 'flugplanung-overview' ||
        this.ansicht === 'sidebar-yield-controlling'
      ) {
        const routeData = this.$router.resolve({ path: '/aufgaben/overview?aufgabenId=' + aufgabe.id });
        window.open(routeData.href, '_blank');
      } else {
        this.$emit('clickAufgabenWidget', aufgabe);
      }
    },
    getTeamLabel(team) {
      if (team.split(' ').length > 1) {
        return team
          .split(' ')
          .map(word => word.substring(0, 1))
          .join('');
      } else return team;
    },
    isFaelligkeitInPast(faelligkeitsdatum) {
      return isBefore(parseISO(faelligkeitsdatum), new Date());
    },
    username(userId) {
      const name = this.$store.getters.getUsernameById(userId);
      const shortenedName = name
        ?.split(' ')
        .map((subName, i) => {
          if (i == 0) return subName;
          else return subName.substring(0, 1) + '.';
        })
        .join(' ');

      return shortenedName;
    },
    plusSignClicked() {
      if (this.ansicht === 'reisetermine-view' || this.ansicht === 'reisetermin-view-releasepruefung') {
        this.$store.commit(SET_AUFGABEN_VERLINKUNG, [
          {
            id: 0,
            typ: 'Reisetermin',
            artifactId: this.reiseterminObj.id,
            artifactText: this.reiseterminObj.reiseterminkuerzel,
          },
        ]);
        if (this.onPlusSignClicked) {
          this.onPlusSignClicked();
        }
      } else {
        console.log('reiseterminObj: ', this.reiseterminObj);
        this.$store.commit(SET_AUFGABEN_VERLINKUNG, [
          {
            id: 0,
            typ: 'Reisetermin',
            artifactId: this.data.id,
            artifactText: this.data.reiseterminkuerzel,
          },
        ]);
        if (this.onPlusSignClicked) {
          this.onPlusSignClicked();
        }
      }
    },
  },
};
</script>
<style scoped>
.aufgabe-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px;
  border: 0.5px solid var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-xs);
  color: var(--text-body);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s, box-shadow 0.15s;
}
.aufgabe-pill:hover {
  background: var(--surface-hover);
  box-shadow: var(--shadow-dropdown);
}
.aufgabe-pill__nummer {
  color: var(--text-muted);
}
.current-aufgabe {
  opacity: 0.5;
  pointer-events: none;
  cursor: default;
}
</style>

<style>
.aufgaben-widget-tooltip.tooltip {
  opacity: 1 !important;
}

.aufgaben-widget-tooltip .tooltip-inner {
  background-color: var(--surface) !important;
  color: #333 !important;
  opacity: 1 !important;
  max-width: 550px;
  max-height: 200px;
  overflow-y: auto;
  text-align: left;
}

.aufgaben-widget-tooltip .tooltip-inner table {
  width: 100%;
  border-collapse: collapse;
}

.aufgaben-widget-tooltip .tooltip-inner td {
  border: 1px solid #ddd;
  padding: 6px 8px;
  vertical-align: top;
}
</style>
